import {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router";
import { LockIcon } from "vega-icons";

import { useDeviceWidth } from "@vega-core/hooks/useDeviceWidth";
import { useGetScrollAndOverflowInfo } from "@vega-core/hooks/useGetScrollAndOverflowInfo";
import { useMobileLandscape } from "@vega-core/hooks/useMobileLandscape";
import { type Tab } from "@vega-core/models/core.type";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { Tooltip } from "../Tooltip/Tooltip";

type UrlSyncConfig = {
  enabled: boolean;
  paramName?: string;
};

type Props = {
  disabled?: boolean;
  fullWidth?: boolean;
  isVisible?: boolean;
  noDataBanner?: ReactElement;
  noSingleTab?: boolean;
  onChange?: (newActiveTab: string) => void;
  small?: boolean;
  innerGap?: boolean;
  tabClassName?: string;
  noMargin?: boolean;
  tabs: Tab[];
  transparentBG?: boolean;
  noContentPadding?: boolean;
  urlSync?: UrlSyncConfig;
  containerClassName?: string;
  tabLineFullWidth?: boolean;
  fullHeight?: boolean;
};

export const TabNavigation = ({
  disabled = false,
  fullWidth: initialFullWidth,
  isVisible = true,
  innerGap = true,
  noDataBanner,
  noSingleTab = false,
  onChange,
  tabClassName,
  small,
  noMargin = false,
  tabs = [],
  transparentBG = false,
  noContentPadding = true,
  urlSync,
  containerClassName,
  tabLineFullWidth = false,
  fullHeight = true,
}: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlParamName = urlSync?.paramName ?? "tab";
  const isMobile = !useDeviceWidth("sm");
  const fullWidth = initialFullWidth || isMobile;

  // Get initial active tab - prioritize URL param if URL sync is enabled
  const getInitialActiveTab = () => {
    if (tabs.length === 0) return "";

    if (urlSync?.enabled) {
      const urlTab = searchParams.get(urlParamName);

      if (urlTab && tabs.some((tab) => tab.index === urlTab && !tab.disabled)) {
        return urlTab;
      }
    }

    return tabs.find((tab) => tab.isActive)?.index ?? tabs[0].index;
  };

  const defaultActiveTabIndex = getInitialActiveTab();

  const [overflowRef, scrollAndOverflowInfo] = useGetScrollAndOverflowInfo();
  const {
    elementIsOverflowingHorizontally,
    elementCanScrollLeft,
    elementCanScrollRight,
  } = scrollAndOverflowInfo;
  const [activeTab, setActiveTab] = useState<string>(defaultActiveTabIndex);
  const isMobileLandscape = useMobileLandscape();
  const [isAnimationEnabled, setIsAnimationEnabled] = useState(false);
  const [widthActiveTab, setWidthActiveTab] = useState(0);
  const [leftActiveTab, setLeftActiveTab] = useState(0);
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const selectedTab = useMemo(
    () => tabs.find((item) => activeTab === item.index),
    [activeTab, tabs],
  );
  const totalCount = useMemo(
    () =>
      tabs.reduce(
        (prev, cur) => (isTruthy(cur.count) ? cur.count + prev : prev),
        0,
      ),
    [tabs],
  );

  const activeTabNode = useMemo(() => {
    if (containerRef) {
      return containerRef.querySelector(
        `[data-index="${activeTab}"]`,
      ) as HTMLDivElement;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, containerRef, defaultActiveTabIndex]);

  const setActiveTabBottomLine = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setWidthActiveTab(node.clientWidth);
      setLeftActiveTab(node.offsetLeft);
    }
  }, []);

  useEffect(() => {
    if (activeTabNode) {
      setActiveTabBottomLine(activeTabNode);
      activeTabNode.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }

    if (!isVisible) {
      setIsAnimationEnabled(false);
    }
  }, [
    isMobileLandscape,
    setActiveTabBottomLine,
    isVisible,
    activeTabNode,
    tabs.length,
    totalCount,
  ]);

  useEffect(() => {
    onChange && onChange(defaultActiveTabIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (newActiveTab: string) => {
    setIsAnimationEnabled(true);
    setActiveTab(newActiveTab);

    // Sync with URL params if enabled
    if (urlSync?.enabled) {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set(urlParamName, newActiveTab);
      setSearchParams(updatedParams, { replace: true });
    }

    onChange && onChange(newActiveTab);
  };

  const getScrollShadowClass = () => {
    if (!isMobile || !elementIsOverflowingHorizontally) {
      return "";
    }

    // We can scroll in both directions (we are in the middle)
    if (elementCanScrollLeft && elementCanScrollRight) {
      return "shadow-leftRight";
    }

    if (elementCanScrollLeft) {
      return "shadow-left";
    }

    if (elementCanScrollRight) {
      return "shadow-right";
    }

    return "";
  };

  useEffect(() => {
    !disabled ? setActiveTab(defaultActiveTabIndex) : undefined;
  }, [defaultActiveTabIndex, disabled]);

  // Effect to sync with URL parameter changes when URL sync is enabled
  useEffect(() => {
    if (urlSync?.enabled && !disabled) {
      const urlTab = searchParams.get(urlParamName);

      if (
        urlTab &&
        tabs.some((tab) => tab.index === urlTab && !tab.disabled) &&
        urlTab !== activeTab
      ) {
        setActiveTab(urlTab);
        onChange && onChange(urlTab);
      }
    }
  }, [
    searchParams,
    urlParamName,
    urlSync?.enabled,
    disabled,
    tabs,
    activeTab,
    onChange,
  ]);

  if (!tabs.length) {
    return null;
  }

  return (
    <>
      {!(tabs.length === 1 && noSingleTab) ? (
        <div
          className={cnTwMerge(
            "relative z-0 w-full overflow-x-scroll sm:w-auto no-scrollbar",
            containerClassName,
            getScrollShadowClass(),
          )}
          ref={overflowRef}
        >
          <div
            className={cnTwMerge(
              "relative scroll-x-auto flex h-[51px] w-full select-none items-end",
              {
                "border-b border-border/primary": !tabLineFullWidth,
                "justify-between": fullWidth && !isMobile,
                "justify-start gap-14": !fullWidth && !isMobile,
                "justify-start gap-8": isMobile,
              },
            )}
            ref={(node) => {
              setContainerRef(node);
            }}
            role="tablist"
          >
            {tabLineFullWidth ? (
              <div className="absolute bottom-0 h-px w-[200vw] -left-[100vw] bg-border/primary" />
            ) : null}
            {tabs.map((tab) => {
              const TabComponent = (
                <div
                  className={cnTwMerge("border-border/primary", {
                    "flex-1": fullWidth && !isMobile,
                  })}
                  key={tab.index}
                >
                  <div
                    className={cnTwMerge(
                      "group z-10 flex flex-col items-center justify-between gap-4 transition duration-300 pt-2 ",
                      {
                        "h-13": isFalsy(small),
                        "h-11": small,
                        "cursor-pointer": isFalsy(tab.disabled),
                        "cursor-not-allowed": isTruthy(tab.disabled),
                        "w-full": fullWidth && !isMobile,
                      },
                    )}
                    data-index={tab.index}
                    key={tab.index}
                    onClick={() =>
                      isFalsy(tab.disabled) && handleChange(tab.index)
                    }
                    role="tab"
                  >
                    <div className="flex flex-row items-center justify-center h-6 gap-2">
                      <div
                        className={cnTwMerge(
                          "flex z-10 whitespace-nowrap text-center body-base-medium leading-6 text-opacity-100 transition",
                          {
                            "group-focus:text-brand-active":
                              !disabled && isFalsy(tab.disabled),
                            "text-brand-default":
                              activeTab === tab.index && isFalsy(tab.disabled),
                            "body-semi-large-medium": isFalsy(small),
                            "body-base-medium": small,
                            "group-hover:text-brand-hover": isFalsy(
                              tab.disabled,
                            ),
                            "text-text/secondary":
                              activeTab !== tab.index && isFalsy(tab.disabled),
                            "text-text/disabled": isTruthy(tab.disabled),
                          },
                          tab.titleClassName,
                        )}
                      >
                        {tab.title}
                      </div>
                      {isTruthy(tab.disabled) ? (
                        <LockIcon className="h-4 w-4 fill-text/disabled" />
                      ) : null}
                      {isTruthy(tab.count) && tab.count > 0 ? (
                        <div
                          className={cnTwMerge(
                            "ml-2 rounded-small text-background/primary flex justify-center items-center h-4.5 min-w-4.5 body-small-semibold",
                            {
                              "bg-brand-default":
                                activeTab === tab.index &&
                                isFalsy(tab.disabled),
                              "bg-text/disabled group-hover:bg-brand-hover":
                                activeTab !== tab.index &&
                                isFalsy(tab.disabled),
                              "bg-text/disabled": isTruthy(tab.disabled),
                            },
                          )}
                        >
                          {tab.count > 99 ? m.common_countMore100() : tab.count}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              );

              // Wrap with tooltip if tooltip text is provided
              if (isTruthy(tab.tooltip)) {
                return (
                  <Tooltip
                    content={tab.tooltip}
                    key={tab.index}
                    providerProps={{ delayDuration: 0 }}
                  >
                    {TabComponent}
                  </Tooltip>
                );
              }

              return TabComponent;
            })}
            <div />
            <div
              className={cnTwMerge(
                "absolute left-0 bottom-0 z-10 h-[1px] bg-brand-default",
                {
                  "transition-all duration-500":
                    isAnimationEnabled && !disabled,
                },
              )}
              style={{
                left: `${leftActiveTab - 1}px`,
                width: `${widthActiveTab}px`,
              }}
            />
          </div>
        </div>
      ) : null}

      <div
        className={cnTwMerge(
          "bg-background/primary rounded-lg px-4 sm:w-auto",
          {
            "mt-6": !noMargin,
            "p-6": innerGap,
            "bg-background/transparent": isTruthy(transparentBG),
            "px-0": noContentPadding,
            "flex flex-1 flex-col": fullHeight,
          },
          tabClassName,
        )}
      >
        {noDataBanner && isFalsy(selectedTab?.count)
          ? noDataBanner
          : selectedTab?.component}
      </div>
    </>
  );
};
